'use client';

import { useEffect, useState } from 'react';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [showReportModal, setShowReportModal] = useState(false);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    console.error('Global error:', error);
  }, [error]);

  const handleSubmitReport = async () => {
    if (!comment.trim()) return;

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/bug-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          comment: `[CRITICAL ERROR PAGE REPORT]\n\nUser description:\n${comment.trim()}\n\nError: ${error?.message || 'Unknown'}\nDigest: ${error?.digest || 'N/A'}`,
          activeFilters: [],
          url: typeof window !== 'undefined' ? window.location.href : 'N/A',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        setSubmitStatus('error');
        return;
      }

      setSubmitStatus('success');
      setTimeout(() => {
        setShowReportModal(false);
        setComment('');
        setSubmitStatus('idle');
      }, 2000);
    } catch (err) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const baseButtonStyle = {
    padding: '0.5rem 0.75rem',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.875rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    border: 'none',
  };

  return (
    <html>
      <body style={{ margin: 0 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '1rem',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            backgroundColor: '#0a0a0a',
            color: '#fafafa',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1rem',
              textAlign: 'center',
              maxWidth: '400px',
            }}
          >
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ef4444"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
              </svg>
            </div>

            <h1 style={{ fontSize: '1.5rem', fontWeight: '600', margin: 0 }}>
              Something went wrong
            </h1>

            <p style={{ color: '#a1a1aa', margin: 0, lineHeight: 1.5 }}>
              We encountered a critical error. Please try refreshing the page.
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                onClick={() => reset()}
                style={{
                  ...baseButtonStyle,
                  backgroundColor: '#22c55e',
                  color: '#000',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                  <path d="M21 3v5h-5" />
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                  <path d="M8 16H3v5" />
                </svg>
                Try again
              </button>

              <button
                onClick={() => (window.location.href = '/')}
                style={{
                  ...baseButtonStyle,
                  backgroundColor: 'transparent',
                  color: '#fafafa',
                  border: '1px solid #27272a',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                Home
              </button>

              <button
                onClick={() => setShowReportModal(true)}
                style={{
                  ...baseButtonStyle,
                  backgroundColor: 'transparent',
                  color: '#a1a1aa',
                  border: '1px solid #27272a',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m8 2 1.88 1.88" />
                  <path d="M14.12 3.88 16 2" />
                  <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
                  <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" />
                  <path d="M12 20v-9" />
                  <path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
                  <path d="M6 13H2" />
                  <path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
                  <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" />
                  <path d="M22 13h-4" />
                  <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
                </svg>
                Report
              </button>
            </div>
          </div>
        </div>

        {/* Bug Report Modal */}
        {showReportModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 50,
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.8)',
                backdropFilter: 'blur(4px)',
              }}
              onClick={() => setShowReportModal(false)}
            />
            <div
              style={{
                position: 'relative',
                zIndex: 50,
                width: '100%',
                maxWidth: '400px',
                margin: '1rem',
                backgroundColor: '#18181b',
                border: '1px solid #27272a',
                borderRadius: '0.5rem',
                padding: '1.5rem',
              }}
            >
              <button
                onClick={() => setShowReportModal(false)}
                style={{
                  position: 'absolute',
                  right: '1rem',
                  top: '1rem',
                  background: 'none',
                  border: 'none',
                  color: '#a1a1aa',
                  cursor: 'pointer',
                  padding: '0.25rem',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                  <path d="m8 2 1.88 1.88" />
                  <path d="M14.12 3.88 16 2" />
                  <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
                  <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" />
                  <path d="M12 20v-9" />
                  <path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
                  <path d="M6 13H2" />
                  <path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
                  <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" />
                  <path d="M22 13h-4" />
                  <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
                </svg>
                <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: '#fafafa' }}>
                  Report this error
                </h3>
              </div>

              <p style={{ color: '#a1a1aa', fontSize: '0.875rem', marginBottom: '1rem' }}>
                Help us fix this issue by describing what you were doing.
              </p>

              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="I was trying to... when this error appeared."
                maxLength={1000}
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '0.75rem',
                  backgroundColor: '#09090b',
                  border: '1px solid #27272a',
                  borderRadius: '0.375rem',
                  color: '#fafafa',
                  fontSize: '0.875rem',
                  resize: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <p style={{ color: '#71717a', fontSize: '0.75rem', margin: '0.25rem 0 1rem' }}>
                {comment.length}/1000
              </p>

              {submitStatus === 'success' && (
                <p style={{ color: '#22c55e', fontSize: '0.875rem', marginBottom: '1rem' }}>
                  ✓ Bug report submitted! Thank you.
                </p>
              )}
              {submitStatus === 'error' && (
                <p style={{ color: '#ef4444', fontSize: '0.875rem', marginBottom: '1rem' }}>
                  Failed to submit. Please email us at hello@quantwheel.com.
                </p>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button
                  onClick={() => setShowReportModal(false)}
                  disabled={isSubmitting}
                  style={{
                    ...baseButtonStyle,
                    backgroundColor: 'transparent',
                    color: '#fafafa',
                    border: '1px solid #27272a',
                    opacity: isSubmitting ? 0.5 : 1,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitReport}
                  disabled={isSubmitting || !comment.trim()}
                  style={{
                    ...baseButtonStyle,
                    backgroundColor: isSubmitting || !comment.trim() ? '#27272a' : '#fafafa',
                    color: isSubmitting || !comment.trim() ? '#71717a' : '#0a0a0a',
                    cursor: isSubmitting || !comment.trim() ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isSubmitting ? 'Sending...' : 'Submit Report'}
                </button>
              </div>
            </div>
          </div>
        )}
      </body>
    </html>
  );
}
